import { AGENT_STATUS_ACCEPTING_CALLS, AGENT_STATUS_RINGING } from 'src/constants/agent-status'

// Must match Activity::ACTIVITY_NAME_IN_POWER_DIALER in api-core. It shares status ON_CALL
// with On Call in agentStatuses, so it can only be found by name.
export const IN_POWER_DIALER_ACTIVITY_NAME = 'In Power Dialer'

// Must match Activity::ACTIVITY_NAME_ON_CALL in api-core. Declining during a Power Dialer
// call lands here, not an available activity, so Twilio can't re-offer this worker before
// api-core's live-task exclusion lands.
export const ON_CALL_ACTIVITY_NAME = 'On Call'

export function findInPowerDialerActivity (agentStatuses) {
  return (agentStatuses || []).find(activity => activity.name === IN_POWER_DIALER_ACTIVITY_NAME)
}

export function findOnCallActivity (agentStatuses) {
  return (agentStatuses || []).find(activity => activity.name === ON_CALL_ACTIVITY_NAME)
}

export function findRingingActivity (agentStatuses) {
  return (agentStatuses || []).find(activity => activity.status === AGENT_STATUS_RINGING)
}

export function findAvailableActivity (agentStatuses) {
  return (agentStatuses || []).find(activity => activity.status === AGENT_STATUS_ACCEPTING_CALLS)
}

// A tab can go stale (backgrounded, network drop) without ever recording a real
// activity change, so a *kept* In Power Dialer record must expire on its own. A
// real push has no keptAt (null) and is trusted regardless of its own age.
// The backend re-pushes In Power Dialer when a reservation timeout flips the worker
// to Available during a live PD call, so the kept value only has to bridge that gap.
export const IN_POWER_DIALER_RECORD_TTL_MS = 5000

export function isInPowerDialerRecordFresh (keptAt, now, ttl = IN_POWER_DIALER_RECORD_TTL_MS) {
  return typeof keptAt !== 'number' || (now - keptAt) < ttl
}

// Twilio moves the worker to Ringing when it offers a reservation and to Available when one
// times out; during a Power Dialer call the agent is still on that call.
export function shouldIgnoreActivityDuringPowerDialerCall (activity, onPowerDialerCall) {
  return Boolean(onPowerDialerCall && activity &&
    [AGENT_STATUS_RINGING, AGENT_STATUS_ACCEPTING_CALLS].includes(activity.status))
}

// Shared by dialer.vue and HubSpotSoftPhone.vue so the two can't diverge. Twilio forces
// Ringing then Available on an offer timeout; with In Power Dialer already recorded,
// that pair is noise from a call still in progress, not a real move off it. Returns the
// next recorded sid plus the keptAt stamp to hold alongside it: a real push clears
// keptAt to null, the keep branch stamps it once (never extends it on a later keep),
// and Ringing touches neither.
export function nextActivityBeforeReservation (recordedSid, previousRawSid, newSid, { ringingSid, availableSid, inPowerDialerSid, keptAt, now, ttl = IN_POWER_DIALER_RECORD_TTL_MS } = {}) {
  if (newSid === ringingSid) {
    return { sid: recordedSid, keptAt }
  }
  if (recordedSid === inPowerDialerSid && previousRawSid === ringingSid && newSid === availableSid &&
      isInPowerDialerRecordFresh(keptAt, now, ttl)) {
    return { sid: recordedSid, keptAt: keptAt == null ? now : keptAt }
  }
  return { sid: newSid, keptAt: null }
}

export function isInboundReservationRow (task) {
  return Boolean(task?.inbound_reservation?.task_sid)
}

// Queue row for an accepted reservation waiting on the PD call's wrap-up. It carries
// communication_id, which the in-queue dedupe, the caller-hung-up splice and the
// queue-refetch preservation key on. callerNumber replaces the task's masked number for
// a user who sees it in full.
export function buildInboundReservationRow (reservation, callerNumber = null) {
  const attributes = reservation?.task?.attributes || {}
  const communicationId = parseInt(attributes.communication_id, 10)
  if (!reservation?.task?.sid || !communicationId) {
    return null
  }
  const contactInfo = attributes.contact_info || {}
  return {
    id: attributes.contact_id ?? null,
    first_name: contactInfo.name || 'Inbound Call',
    last_name: '',
    phone_number: callerNumber || contactInfo.phone || null,
    company_name: null,
    communication_id: communicationId,
    inbound_reservation: {
      task_sid: reservation.task.sid,
      task_type: attributes.task_type,
      reservation_sid: reservation.sid
    }
  }
}

// A queue refetch returns only list items; rows that carry a communication_id (waiting
// inbound callers) exist only client-side, so they go back on top of the fresh list.
export function preserveIncomingCallRows (previousQueue, newQueue) {
  const incomingCalls = (previousQueue || []).filter(task => task && typeof task === 'object' && 'communication_id' in task)
  return [...incomingCalls, ...newQueue]
}
